// Revise run — the results screen, per profile.

import type { ReviseRun } from '../../../../../lib/revise/types';
import { tagSetsFor } from '../../../../../lib/revise/tags';
import type { ReviseRunApi } from './useReviseRun';

const barClass = (pct: number) => (pct >= 70 ? 'rr-hi' : pct >= 40 ? 'rr-mid' : 'rr-lo');

export function Results({ run, api, sibling }: { run: ReviseRun; api: ReviseRunApi; sibling?: Record<string, unknown> | null }) {
  const s = api.summary;
  const tags = tagSetsFor(run.board, run.profile).flatMap((t) => [...t.values]).filter((t) => s.byTag[t]);
  const unanswered = s.assessed - s.answered;
  const nscLevels = s.levels
    ? ([
        ['Finding (L1)', s.levels.L1],
        ['Explaining (L2)', s.levels.L2],
        ['Judging (L3)', s.levels.L3],
      ] as const)
    : null;
  const before = run.check?.sitting === 'after' && sibling && typeof sibling.pct === 'number' ? sibling : null;

  return (
    <div className="rr-results">
      <span className="rr-eyebrow">{run.results.eyebrow}</span>
      <h1>{run.results.title}</h1>
      <div className="rr-score">
        <b>{s.total > 0 ? `${s.earned}/${s.total}` : `${s.pct}%`}</b>
        <span>
          {s.total > 0 ? `${s.pct}%` : `${Math.round((s.pct / 100) * s.assessed)} of ${s.assessed} right`}
          {s.nscLevel ? ` · Level ${s.nscLevel}` : ''}
        </span>
      </div>
      {unanswered > 0 && <div className="rr-note">{unanswered} question{unanswered === 1 ? '' : 's'} left unanswered. They count as wrong.</div>}
      {s.band && (
        <div className="rr-band">
          <b>{s.band.label}</b>
          {s.band.text}
        </div>
      )}

      {before && (
        <div>
          <h2>Before and after</h2>
          <div className="rr-compare">
            <div className="rr-card">
              <span className="rr-eyebrow">Before the lessons</span>
              <b>{String(before.pct)}%</b>
            </div>
            <div className="rr-card">
              <span className="rr-eyebrow">After</span>
              <b>{s.pct}%</b>
            </div>
          </div>
        </div>
      )}

      {nscLevels ? (
        <div>
          <h2>Your marks by kind of thinking</h2>
          <div className="rr-bars">
            {nscLevels.map(([label, v]) => {
              const pct = v.total ? Math.round((v.earned / v.total) * 100) : 0;
              return (
                <div key={label} className="rr-bar">
                  <span>{label}</span>
                  <i>
                    <b className={barClass(pct)} style={{ width: `${pct}%` }} />
                  </i>
                  <span>
                    {v.earned}/{v.total}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ) : tags.length > 0 ? (
        <div>
          <h2>{run.profile === 'check' ? 'By tier' : 'By skill'}</h2>
          <div className="rr-bars">
            {tags.map((t) => {
              const v = s.byTag[t];
              const pct = v.n ? Math.round((v.right / v.n) * 100) : 0;
              return (
                <div key={t} className="rr-bar">
                  <span>{t}</span>
                  <i>
                    <b className={barClass(pct)} style={{ width: `${pct}%` }} />
                  </i>
                  <span>
                    {v.right}/{v.n}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      {s.wrong.length > 0 && (
        <div>
          <h2>To go back over</h2>
          <div className="rr-recap">
            {s.wrong.map((w) => (
              <div key={w.id} className="rr-recap-row rr-no">
                <span>{w.number ? `${w.number} · ` : ''}{w.short}</span>
                <span>{w.marks ? `${w.marks} mark${w.marks === 1 ? '' : 's'}` : ''}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {s.slow.length > 0 && run.profile === 'paper' && (
        <div>
          <h2>Where the time went</h2>
          <div className="rr-recap">
            {s.slow.map((x) => (
              <div key={x.id} className="rr-recap-row">
                <span>{x.short}</span>
                <span>{Math.round(x.seconds)}s</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {run.results.done && (run.results.done.title || run.results.done.text) && (
        <div className="rr-card">
          {run.results.done.label && <span className="rr-eyebrow">{run.results.done.label}</span>}
          {run.results.done.title && <h3>{run.results.done.title}</h3>}
          {run.results.done.text && <p>{run.results.done.text}</p>}
        </div>
      )}
      {run.results.footer && <p className="rr-note">{run.results.footer}</p>}

      <div className="rr-nav" style={{ borderTop: 0, background: 'transparent', padding: 0 }}>
        <div className="rr-nav-right">
          {s.wrong.length > 0 && (
            <button type="button" onClick={api.startReview}>
              Review wrong answers
            </button>
          )}
          <button type="button" onClick={api.restart}>
            Take it again
          </button>
          {api.onAdvance && (
            <button type="button" className="rr-btn-primary" onClick={api.onAdvance}>
              Continue
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
